import { useEffect, useRef, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { ChevronDown, Phone, Video, Code2 } from "lucide-react";
import { ROUND_META, SKILLS, PRESETS, rebalance, fullScoring, presetScoring, enabledKeys } from "./scoringModel";

const ICON = { telephonic: Phone, video: Video, coding: Code2 };

function WeightSlider({ label, value, onChange, disabled, testid, strong }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_3.5rem] sm:grid-cols-[11rem_minmax(0,1fr)_3.5rem] items-center gap-x-4 gap-y-1.5" data-testid={testid}>
      <span className={`text-sm truncate col-span-2 sm:col-span-1 ${strong ? "font-medium text-slate-900" : "text-slate-700"}`}>{label}</span>
      <SliderPrimitive.Root value={[value]} min={0} max={100} step={5} disabled={disabled} onValueChange={([v]) => onChange(v)}
        className="relative flex w-full touch-none select-none items-center h-6 data-[disabled]:opacity-60" aria-label={label}>
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-slate-200">
          <SliderPrimitive.Range className={`absolute h-full ${strong ? "bg-indigo-500" : "bg-indigo-300"}`} />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb data-testid={`${testid}-thumb`}
          className="block h-5 w-5 rounded-full border-2 border-slate-800 bg-amber-400 shadow-sm transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-200 cursor-grab active:cursor-grabbing" />
      </SliderPrimitive.Root>
      <span className="text-sm font-mono font-semibold text-slate-900 text-right tabular-nums" data-testid={`${testid}-value`}>{value}%</span>
    </div>
  );
}

function SkillGroup({ round, skills, onChange, prefix }) {
  const [open, setOpen] = useState(false);
  const Icon = ICON[round.k];
  const keys = SKILLS[round.k].map(([k]) => k);
  return (
    <div className="rounded-lg border border-[color:var(--bd-sub)] bg-white">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`${prefix}-skills-${round.k}-toggle`}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 rounded-lg transition-colors">
        <Icon className="w-4 h-4 text-indigo-500" />
        <span className="font-medium text-slate-800 whitespace-nowrap shrink-0">{round.label}</span>
        <span className="text-xs text-slate-500 truncate min-w-0">· {SKILLS[round.k].filter(([k]) => skills[k] > 0).map(([k, l]) => `${l} ${skills[k]}%`).join(", ")}</span>
        <ChevronDown className={`w-4 h-4 ml-auto shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3" data-testid={`${prefix}-skills-${round.k}`}>
          {SKILLS[round.k].map(([k, l]) => (
            <WeightSlider key={k} label={l} value={skills[k]} testid={`${prefix}-skill-${round.k}-${k}`}
              onChange={(v) => onChange(rebalance(skills, keys, k, v))} />
          ))}
        </div>
      )}
    </div>
  );
}

// Round weights (overall score) + skill weights inside each round. Every group always totals 100%.
export default function JobScoringEditor({ value, onChange, stages, prefix = "job" }) {
  const keys = enabledKeys(stages);
  const s = fullScoring(value, stages);
  const sig = keys.join(",");
  const prevSig = useRef(sig);
  useEffect(() => {
    if (prevSig.current !== sig && value) onChange(fullScoring({ ...value, rounds: {} }, stages));
    prevSig.current = sig;
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  const rounds = ROUND_META.filter((r) => stages?.[r.k]);
  if (!rounds.length) return <p className="text-sm text-slate-500">Turn on at least one interview round to set its weight.</p>;

  return (
    <div className="space-y-5" data-testid={`${prefix}-scoring`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500 mr-1">Quick presets:</span>
        {PRESETS.map(([k, l]) => (
          <button type="button" key={k} data-testid={`${prefix}-scoring-preset-${k}`} onClick={() => onChange(presetScoring(k, stages))}
            className="text-xs px-3 py-1.5 rounded-full border border-[color:var(--bd-sub)] text-slate-700 hover:border-indigo-400 hover:text-indigo-600 transition-colors">{l}</button>
        ))}
      </div>
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Round weights · overall score</div>
          <div className="text-xs text-slate-500" data-testid={`${prefix}-scoring-total`}>Total {keys.reduce((a, k) => a + s.rounds[k], 0)}%</div>
        </div>
        <div className="space-y-3">
          {rounds.map((r) => (
            <WeightSlider key={r.k} strong label={r.label} value={s.rounds[r.k]} disabled={rounds.length === 1} testid={`${prefix}-round-${r.k}`}
              onChange={(v) => onChange({ ...s, rounds: { ...s.rounds, ...rebalance(s.rounds, keys, r.k, v) } })} />
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Skill weights · inside each round</div>
        <div className="space-y-2">
          {rounds.map((r) => (
            <SkillGroup key={r.k} round={r} skills={s.skills[r.k]} prefix={prefix}
              onChange={(sk) => onChange({ ...s, skills: { ...s.skills, [r.k]: sk } })} />
          ))}
        </div>
      </div>
    </div>
  );
}
